import { careerDetailPageQueryOptions } from "@data/publicPageDataQuery"
import CareersCard from "@features/careers/CareersCard"
import { sanitizeRichHtml } from "@lib/sanitizeHtml"
import * as m from "@paraglide/messages"
import { imageSizes, imageSrcSet, imageUrl } from "@shared/lib/componentUtils"
import { AstroCareersViewTransitions } from "@shared/ui/AstroViewTransitions"
import { useRouteShellContext } from "@shared/ui/routeShellContext"
import { BackButton, SiteShell } from "@shared/ui/SiteShell"
import type { CareerItem } from "@site-types/content"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { isSupportedLocale } from "@utils/locale"
import { localizedRouteHead } from "@utils/routeHead"

export const Route = createFileRoute("/$locale/careers/$slug")({
  beforeLoad: ({ params }) => {
    if (!isSupportedLocale(params.locale)) throw redirect({ to: "/404" })
  },
  loader: async ({ context, params }) => {
    const locale = params.locale as "en" | "ja"
    const data = await context.queryClient.ensureQueryData(careerDetailPageQueryOptions(locale, params.slug))
    if (!data) throw redirect({ href: `/${locale}/careers` })
    return data
  },
  head: ({ params, loaderData }) => {
    const locale = params.locale as "en" | "ja"
    const position = loaderData?.position
    const head = localizedRouteHead({
      locale,
      page: "careers",
      path: `/${locale}/careers/${params.slug}`,
      title: position?.title ? `${position.title} | PLAYBRAIN Inc.` : undefined,
      description: position?.newsBlurb,
      image: position?.newsCoverImage
        ? (imageUrl(position.newsCoverImage, "careerCard") ?? undefined)
        : undefined,
      type: "article",
    })
    if (position?.newsCoverImage) {
      head.links.push({
        rel: "preload",
        as: "image",
        href: imageUrl(position.newsCoverImage, "careerCard") ?? "",
        imageSrcSet: imageSrcSet(position.newsCoverImage, "careerCard"),
        imageSizes: imageSizes("careerCard"),
        fetchPriority: "high",
      })
    }
    return head
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { position } = Route.useLoaderData()
  return (
    <SiteShell>
      <CareerDetailPage position={position} />
    </SiteShell>
  )
}

export function CareerDetailPage({ position }: { position: CareerItem }) {
  const { locale } = useRouteShellContext()
  return (
    <>
      <AstroCareersViewTransitions careers={[position]} />
      <div className="relative z-[99] m-auto max-w-[1300px] px-[30px] py-[2rem]">
        <BackButton />
      </div>
      <div className="relative z-[5] mx-auto grid max-w-[1300px] grid-cols-[2fr_5fr] gap-[30px] px-[30px] pt-[30px] pb-[50px] text-[var(--white)] max-sm:grid-cols-1 max-careers-mid:mx-0 max-careers:grid-cols-[1fr_2fr]">
        <div className="row-[1/span_2] max-sm:col-[1/span_1] max-sm:row-[1/span_1] max-sm:w-[200px] max-sm:justify-self-center max-careers:row-[1/span_1]">
          <CareersCard position={position} />
        </div>
        <div className="col-start-2 max-sm:col-[1/span_1]">
          <h1 className="text-[2rem] [text-wrap:pretty] md:text-[3rem]">{position.title}</h1>
          <p className="mt-[0.6rem] mb-[1.6rem] block max-w-none bg-[var(--white-30)] p-[3rem] text-[2rem] text-[var(--white)] italic [--clip-inset:20px] [clip-path:polygon(0%_0%,calc(100%_-_var(--clip-inset))_0%,100%_var(--clip-inset),100%_100%,var(--clip-inset)_100%,0%_calc(100%_-_var(--clip-inset)))]">
            {position.newsBlurb}
          </p>
        </div>
        <article className="post relative z-[5] col-start-2 m-auto max-w-[var(--max-width-text)] text-[1.4rem] text-[var(--white)] max-sm:col-[1/span_1] max-careers:col-[1/span_2] [&_a:hover]:text-[var(--pink)] [&_a:hover]:underline [&_a:hover]:[text-underline-offset:6px] [&_a]:text-[length:var(--p-fontSize)] [&_a]:text-[var(--cyan)] md:[&_a]:text-[length:2rem] [&_img]:m-auto [&_img]:max-w-full [&_p]:mr-0 [&_p]:mb-[2rem] [&_p]:ml-0 [&_p]:text-[length:var(--p-fontSize)] [&_p]:leading-[150%] [&_p]:tracking-[0.2px] [&_p]:[text-wrap:pretty] md:[&_p]:text-[length:2rem] [&_ul]:mb-[2rem] [&_ul]:grid [&_ul]:pl-[25px] [&_ul]:text-[length:var(--p-fontSize)] [&_ul]:text-[var(--white)] md:[&_ul]:text-[length:2rem]">
          <div dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(position.description) }} />
          <a
            className="apply-link text-[var(--white)] no-underline"
            href={`/${locale}/contact/${position.slug?.current}`}
          >
            <div className="relative mt-[3rem] block max-sm:col-[1/span_1] max-careers:col-[1/span_2]">
              <div className="bg-[var(--blue)] p-[3rem] [--clip-inset:20px] [clip-path:polygon(0%_0%,calc(100%_-_var(--clip-inset))_0%,100%_var(--clip-inset),100%_100%,var(--clip-inset)_100%,0%_calc(100%_-_var(--clip-inset)))] hover:bg-[var(--pink)]">
                <h3>{m.careers_post_apply({}, { locale: locale }).toUpperCase()}</h3>
                <p className="m-0">{m.careers_post_apply_description({}, { locale: locale })}</p>
              </div>
            </div>
          </a>
        </article>
      </div>
    </>
  )
}
